import React, { useState, useEffect } from "react";
import { axiosInstance } from "../../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Edit, Trash2, Users, Download } from "lucide-react";
import { toast } from "sonner";

const ROLE_DISPLAY = {
  superadmin: "Super Admin",
  marketing: "Marketing",
  follow_up: "Follow Up",
  supervisor: "Supervisor",
};

const ITEMS_PER_PAGE = 10;

export const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [websiteList, setWebsiteList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [userForm, setUserForm] = useState({
    email: "",
    password: "",
    name: "",
    role: "marketing",
    accessible_websites: [],
  });
  const [editingUser, setEditingUser] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);

  const [searchName, setSearchName] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchUsers();
    fetchWebsiteList();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get("/users");
      setUsers(response.data);
    } catch (error) {
      toast.error("Gagal memuat data users");
    }
  };

  const fetchWebsiteList = async () => {
    try {
      const response = await axiosInstance.get("/website-list");
      setWebsiteList(response.data);
    } catch (error) {
      toast.error("Gagal memuat data website");
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingUser) {
        await axiosInstance.put(`/users/${editingUser.id}`, {
          name: userForm.name,
          role: userForm.role,
          accessible_websites: userForm.accessible_websites,
          ...(userForm.password ? { password: userForm.password } : {}),
        });
        toast.success("User berhasil diupdate");
      } else {
        await axiosInstance.post("/auth/register", userForm);
        toast.success("User berhasil ditambahkan");
      }
      setShowUserDialog(false);
      setUserForm({ email: "", password: "", name: "", role: "marketing", accessible_websites: [] });
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal menyimpan user");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Yakin ingin menghapus user ini?")) return;
    try {
      await axiosInstance.delete(`/users/${userId}`);
      toast.success("User berhasil dihapus");
      fetchUsers();
    } catch (error) {
      toast.error("Gagal menghapus user");
    }
  };

  const openEditUser = (u) => {
    setEditingUser(u);
    setUserForm({
      email: u.email,
      password: "",
      name: u.name,
      role: u.role,
      accessible_websites: u.accessible_websites || [],
    });
    setShowUserDialog(true);
  };

  const toggleWebsite = (websiteName) => {
    setUserForm((prev) => {
      const websites = prev.accessible_websites || [];
      return websites.includes(websiteName)
        ? { ...prev, accessible_websites: websites.filter((w) => w !== websiteName) }
        : { ...prev, accessible_websites: [...websites, websiteName] };
    });
  };

  const toggleUserStatus = async (u) => {
    const newStatus = u.status === "locked" ? "active" : "locked";
    const action = newStatus === "locked" ? "lock" : "unlock";
    if (!window.confirm(`Yakin ingin ${action} user ${u.name}?`)) return;
    try {
      await axiosInstance.put(`/users/${u.id}/status`, { status: newStatus });
      toast.success(`User berhasil di-${action}`);
      fetchUsers();
    } catch (error) {
      toast.error(`Gagal ${action} user`);
    }
  };

  const toggleExportPermission = async (u) => {
    const newPermission = !u.can_export;
    const action = newPermission ? "mengaktifkan" : "menonaktifkan";
    if (!window.confirm(`Yakin ingin ${action} akses download untuk ${u.name}?`)) return;
    try {
      await axiosInstance.put(`/users/${u.id}/export-permission`, { can_export: newPermission });
      toast.success(`Akses download berhasil di${action === "mengaktifkan" ? "aktifkan" : "nonaktifkan"}`);
      fetchUsers();
    } catch (error) {
      toast.error("Gagal mengubah akses download");
    }
  };

  const filteredUsers = users.filter((u) => {
    const q = searchName.toLowerCase();
    const matchName = u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    const matchRole = filterRole === "all" || u.role === filterRole;
    const matchStatus = filterStatus === "all" || u.status === filterStatus;
    return matchName && matchRole && matchStatus;
  });

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  const resetPage = () => setCurrentPage(1);

  return (
    <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30" data-testid="users-tab-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-red-50">Manajemen User</CardTitle>
            <CardDescription className="text-gray-400">Kelola user dan role akses</CardDescription>
          </div>
          <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setEditingUser(null);
                  setUserForm({ email: "", password: "", name: "", role: "marketing", accessible_websites: [] });
                }}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
                data-testid="add-user-button"
              >
                <Plus className="mr-2" size={18} />
                Tambah User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a1a1b] border-red-900/30">
              <DialogHeader>
                <DialogTitle className="text-red-50">
                  {editingUser ? "Edit User" : "Tambah User Baru"}
                </DialogTitle>
                <DialogDescription className="text-gray-400">
                  {editingUser ? "Update informasi user" : "Buat user baru dengan role tertentu"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUserSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="user-email" className="text-gray-300">Email</Label>
                  <Input
                    id="user-email"
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    disabled={!!editingUser}
                    required
                    className="bg-[#0f0f10] border-red-900/30 text-white"
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-name" className="text-gray-300">Nama</Label>
                  <Input
                    id="user-name"
                    value={userForm.name}
                    onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                    required
                    className="bg-[#0f0f10] border-red-900/30 text-white"
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-password" className="text-gray-300">
                    Password {editingUser && "(kosongkan jika tidak diubah)"}
                  </Label>
                  <Input
                    id="user-password"
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    required={!editingUser}
                    className="bg-[#0f0f10] border-red-900/30 text-white"
                    data-testid="user-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-role" className="text-gray-300">Role</Label>
                  <Select value={userForm.role} onValueChange={(value) => setUserForm({ ...userForm, role: value })}>
                    <SelectTrigger className="bg-[#0f0f10] border-red-900/30 text-white" data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1b] border-red-900/30 text-white">
                      <SelectItem value="marketing" className="text-white hover:bg-red-900/20 focus:bg-red-900/30 focus:text-white">Marketing</SelectItem>
                      <SelectItem value="follow_up" className="text-white hover:bg-red-900/20 focus:bg-red-900/30 focus:text-white">Follow Up</SelectItem>
                      <SelectItem value="supervisor" className="text-white hover:bg-red-900/20 focus:bg-red-900/30 focus:text-white">Supervisor</SelectItem>
                      <SelectItem value="superadmin" className="text-white hover:bg-red-900/20 focus:bg-red-900/30 focus:text-white">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(userForm.role === "marketing" || userForm.role === "follow_up" || userForm.role === "supervisor") && (
                  <div className="space-y-3">
                    <Label className="text-gray-300">Website yang Dapat Diakses</Label>
                    <div className="space-y-2 max-h-40 overflow-y-auto p-3 bg-[#0f0f10] rounded-lg border border-red-900/30">
                      {websiteList.length === 0 ? (
                        <p className="text-sm text-gray-400">Belum ada website. Tambahkan di tab Website.</p>
                      ) : (
                        websiteList.map((website) => (
                          <div key={website.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={"website-" + website.id}
                              checked={(userForm.accessible_websites || []).includes(website.name)}
                              onCheckedChange={() => toggleWebsite(website.name)}
                              className="border-red-600 data-[state=checked]:bg-red-600"
                            />
                            <label htmlFor={"website-" + website.id} className="text-sm text-white cursor-pointer">
                              {website.name}
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
                  disabled={loading}
                  data-testid="save-user-button"
                >
                  {loading ? "Menyimpan..." : editingUser ? "Update" : "Tambah"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter Section */}
        <div className="mb-6 p-4 bg-[#0f0f10] rounded-lg border border-red-900/30">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search-name" className="text-gray-300">Cari Nama/Email</Label>
              <Input
                id="search-name"
                placeholder="Ketik nama atau email..."
                value={searchName}
                onChange={(e) => {
                  setSearchName(e.target.value);
                  resetPage();
                }}
                className="bg-[#1a1a1b] border-red-900/30 text-white placeholder:text-gray-500"
                data-testid="user-search-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="filter-role" className="text-gray-300">Filter Role</Label>
              <Select
                value={filterRole}
                onValueChange={(value) => {
                  setFilterRole(value);
                  resetPage();
                }}
              >
                <SelectTrigger id="filter-role" className="bg-[#1a1a1b] border-red-900/30 text-white" data-testid="user-filter-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1b] border-red-900/30">
                  <SelectItem value="all" className="text-white hover:bg-red-900/20">Semua Role</SelectItem>
                  <SelectItem value="superadmin" className="text-white hover:bg-red-900/20">Super Admin</SelectItem>
                  <SelectItem value="marketing" className="text-white hover:bg-red-900/20">Marketing</SelectItem>
                  <SelectItem value="follow_up" className="text-white hover:bg-red-900/20">Follow Up</SelectItem>
                  <SelectItem value="supervisor" className="text-white hover:bg-red-900/20">Supervisor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="filter-status" className="text-gray-300">Filter Status</Label>
              <Select
                value={filterStatus}
                onValueChange={(value) => {
                  setFilterStatus(value);
                  resetPage();
                }}
              >
                <SelectTrigger id="filter-status" className="bg-[#1a1a1b] border-red-900/30 text-white" data-testid="user-filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1b] border-red-900/30">
                  <SelectItem value="all" className="text-white hover:bg-red-900/20">Semua Status</SelectItem>
                  <SelectItem value="active" className="text-white hover:bg-red-900/20">Active</SelectItem>
                  <SelectItem value="locked" className="text-white hover:bg-red-900/20">Locked</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-400" data-testid="user-count-label">
            Menampilkan {filteredUsers.length} dari {users.length} user
          </div>
        </div>

        {/* User List */}
        <div className="space-y-4">
          {currentUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">Tidak ada user ditemukan</p>
              <p className="text-gray-500 text-sm mt-2">Coba ubah filter pencarian</p>
            </div>
          ) : (
            currentUsers.map((u) => (
              <div
                key={u.id}
                className="flex items-center justify-between p-4 bg-[#0f0f10] rounded-lg border border-red-900/20"
                data-testid={`user-item-${u.id}`}
              >
                <div className="flex-1">
                  <div className="font-semibold text-white">{u.name}</div>
                  <div className="text-sm text-gray-400">{u.email}</div>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <span className="inline-block px-2 py-1 bg-red-900/30 text-red-400 text-xs rounded border border-red-700/50">
                      {ROLE_DISPLAY[u.role] || u.role}
                    </span>
                    <span
                      className={`inline-block px-2 py-1 text-xs rounded border ${
                        u.status === "locked"
                          ? "bg-red-900/30 text-red-400 border-red-700/50"
                          : "bg-green-900/30 text-green-400 border-green-700/50"
                      }`}
                    >
                      {u.status === "locked" ? "🔒 Locked" : "✓ Active"}
                    </span>
                    {u.role === "supervisor" && (
                      <span
                        className={`inline-block px-2 py-1 text-xs rounded border ${
                          u.can_export
                            ? "bg-blue-900/30 text-blue-400 border-blue-700/50"
                            : "bg-gray-900/30 text-gray-400 border-gray-700/50"
                        }`}
                      >
                        {u.can_export ? "📥 Download ON" : "🚫 Download OFF"}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {u.role === "supervisor" && (
                    <Button
                      onClick={() => toggleExportPermission(u)}
                      variant="outline"
                      size="sm"
                      className={`${
                        u.can_export
                          ? "border-gray-700/50 text-gray-400 hover:bg-gray-900/20"
                          : "border-blue-700/50 text-blue-400 hover:bg-blue-900/20"
                      }`}
                      title={u.can_export ? "Nonaktifkan Download" : "Aktifkan Download"}
                      data-testid={`toggle-export-${u.id}`}
                    >
                      <Download size={16} />
                    </Button>
                  )}
                  <Button
                    onClick={() => toggleUserStatus(u)}
                    variant="outline"
                    size="sm"
                    className={`${
                      u.status === "locked"
                        ? "border-green-700/50 text-green-400 hover:bg-green-900/20"
                        : "border-red-700/50 text-red-400 hover:bg-red-900/20"
                    }`}
                    data-testid={`toggle-status-${u.id}`}
                  >
                    {u.status === "locked" ? "Unlock" : "Lock"}
                  </Button>
                  <Button
                    onClick={() => openEditUser(u)}
                    variant="ghost"
                    size="sm"
                    className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                    data-testid={`edit-user-${u.id}`}
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    onClick={() => handleDeleteUser(u.id)}
                    variant="ghost"
                    size="sm"
                    className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                    data-testid={`delete-user-${u.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-red-900/30">
            <div className="text-sm text-gray-400">
              Menampilkan {startIndex + 1} - {Math.min(endIndex, filteredUsers.length)} dari {filteredUsers.length} user
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                variant="outline"
                size="sm"
                className="border-red-900/30 text-white hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="user-prev-page"
              >
                Sebelumnya
              </Button>
              <div className="flex items-center gap-2 px-3 py-1 bg-[#0f0f10] border border-red-900/30 rounded text-white text-sm">
                <span>Halaman {currentPage} dari {totalPages}</span>
              </div>
              <Button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                variant="outline"
                size="sm"
                className="border-red-900/30 text-white hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="user-next-page"
              >
                Selanjutnya
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UsersTab;
